import {
  RUNTIME_OFFER_PLACEMENT_DASHA_CALCULATOR_REPORT_BRIDGE,
  RUNTIME_OFFER_PLACEMENT_HI_DASHA_CALCULATOR_REPORT_BRIDGE,
} from "@/lib/offer-placement-runtime-keys.generated";
import type { ReactNode } from "react";
import CalculatorReportBridge from "@/components/CalculatorReportBridge";
import type { KundaliResponse } from "@/lib/api/kundali";
import { formatCivilDate } from "@/lib/civil-date";
import { DASHA_DETAILS, formatNakshatra } from "./_data";

export { DashaSeoContent } from "./_seo-content";

type KundaliDasha = NonNullable<KundaliResponse["dasha"]>;
type EnginePeriod = KundaliDasha["all_mahadashas"][number];

const COPY = {
  en: {
    eyebrow: "Vimshottari Dasha",
    title: (name: string) => `Dasha periods for ${name}`,
    moonNakshatra: "Your Moon Nakshatra",
    moonIn: "Moon in",
    birthLord: "Birth Mahadasha Lord",
    currentHeading: "Your current Dasha",
    currentMahadasha: "Current Mahadasha",
    currentAntardasha: "Current Antardasha",
    antardashaHeading: (lord: string) => `Antardashas inside your ${lord} Mahadasha`,
    antardashaBody:
      "Every mahadasha subdivides into nine antardashas in the same fixed order. These start and end dates are computed from your own mahadasha boundaries, so they are yours — not the generic sub-period table.",
    sequenceHeading: "Your engine-calculated Mahadasha timeline",
    sequenceBody:
      "These period boundaries come from your birth Moon's exact sidereal longitude, not a generic planet-order lookup.",
    activeNow: "Active now",
    years: "years",
    approximate:
      "Birth time was not provided, so Moon-based period boundaries are approximate. Recalculate with an exact birth time before using a boundary date for a decision.",
    unavailable: "The Dasha engine did not return a period timeline. Please recalculate.",
  },
  hi: {
    eyebrow: "विंशोत्तरी दशा",
    title: (name: string) => `${name} की दशा अवधि`,
    moonNakshatra: "आपका चंद्र नक्षत्र",
    moonIn: "चंद्र राशि",
    birthLord: "जन्म महादशा स्वामी",
    currentHeading: "आपकी वर्तमान दशा",
    currentMahadasha: "वर्तमान महादशा",
    currentAntardasha: "वर्तमान अंतर्दशा",
    antardashaHeading: (lord: string) => `आपकी ${lord} महादशा की अंतर्दशाएं`,
    antardashaBody:
      "प्रत्येक महादशा उन्हीं नौ ग्रहों की अंतर्दशाओं में बँटती है। ये तिथियाँ आपकी अपनी महादशा-सीमाओं से निकली हैं — किसी सामान्य उप-अवधि तालिका से नहीं।",
    sequenceHeading: "इंजन से गणित महादशा कालक्रम",
    sequenceBody:
      "ये अवधि-सीमाएं आपके जन्म चंद्रमा के सटीक निरयण देशांतर से निकली हैं, किसी सामान्य ग्रह-क्रम सूची से नहीं।",
    activeNow: "अभी सक्रिय",
    years: "वर्ष",
    approximate:
      "जन्म समय नहीं दिया गया था, इसलिए चंद्र-आधारित अवधि सीमाएं अनुमानित हैं। किसी निर्णय में सीमा-तिथि उपयोग करने से पहले सटीक जन्म समय के साथ दोबारा गणना करें।",
    unavailable: "दशा इंजन ने अवधि-कालक्रम नहीं लौटाया। कृपया दोबारा गणना करें।",
  },
} as const;

export function DashaResult({
  result,
  defaultLanguage = "en",
  crossLinks,
}: {
  result: KundaliResponse;
  defaultLanguage?: "en" | "hi";
  crossLinks: ReactNode;
}) {
  const dasha = result.dasha;
  const copy = COPY[defaultLanguage];
  if (!dasha) return <p role="alert">{copy.unavailable}</p>;

  const sourcePath = defaultLanguage === "hi" ? "/hi/dasha-calculator" : "/dasha-calculator";
  const bridgePlacement =
    defaultLanguage === "hi"
      ? RUNTIME_OFFER_PLACEMENT_HI_DASHA_CALCULATOR_REPORT_BRIDGE
      : RUNTIME_OFFER_PLACEMENT_DASHA_CALCULATOR_REPORT_BRIDGE;

  return (
    <div className="sacred-bg min-h-[calc(100vh-130px)]">
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-12">
        <DashaHeader name={String(result.birth_details.name)} language={defaultLanguage} />
        {!result.birth_time_known && <ApproximationNotice text={copy.approximate} />}
        <MoonNakshatraCard result={result} dasha={dasha} language={defaultLanguage} />
        <CurrentDashaCard dasha={dasha} language={defaultLanguage} />
        <CalculatorReportBridge
          planId="mahadasha_forecast"
          source={sourcePath}
          intent="chart_report"
          placement={bridgePlacement}
          title={
            defaultLanguage === "hi"
              ? "Mahadasha Forecast me 12-month timing dekhein"
              : "Turn this current dasha into a 12-month Mahadasha Forecast"
          }
          body={
            defaultLanguage === "hi"
              ? "Calculator vartamaan mahadasha aur antardasha dikhata hai. Forecast me transition, decision windows aur graha-specific upay aapki kundali se milte hain."
              : "The calculator shows your current mahadasha and antardasha. The forecast adds transitions, decision windows, and graha-specific upay from your chart."
          }
          cta={defaultLanguage === "hi" ? "Mahadasha Forecast paayein" : "Get Mahadasha Forecast"}
          language={defaultLanguage}
        />
        <AntardashaSequence dasha={dasha} language={defaultLanguage} />
        <DashaSequence dasha={dasha} language={defaultLanguage} />
        {crossLinks}
      </div>
    </div>
  );
}

function DashaHeader({ name, language }: { name: string; language: "en" | "hi" }) {
  const copy = COPY[language];
  return (
    <div className="text-center mb-8">
      <p className="text-xs font-semibold uppercase tracking-[0.25em] mb-3 text-intent-brand">
        {copy.eyebrow}
      </p>
      <h2 className="text-3xl sm:text-4xl font-bold leading-tight font-serif text-ish-dark">
        {copy.title(name)}
      </h2>
    </div>
  );
}

function ApproximationNotice({ text }: { text: string }) {
  return (
    <p
      role="note"
      className="mb-6 rounded-lg border border-warning/30 bg-warning/10 p-4 text-sm text-ish-dark"
    >
      {text}
    </p>
  );
}

function MoonNakshatraCard({
  result,
  dasha,
  language,
}: {
  result: KundaliResponse;
  dasha: KundaliDasha;
  language: "en" | "hi";
}) {
  const copy = COPY[language];
  return (
    <div className="card-sacred p-6 mb-6 text-center">
      <p className="text-sm font-semibold uppercase tracking-wide mb-2 text-ish-text-faint">
        {copy.moonNakshatra}
      </p>
      <p className="text-2xl font-bold font-serif text-ish-dark">
        {formatNakshatra(dasha.moon_nakshatra)}
      </p>
      <p className="text-sm mt-2 text-ish-text-muted">
        {copy.moonIn} {formatNakshatra(result.moon_rashi)}
      </p>
      <div className="mt-4 inline-block rounded-lg bg-ish-accent-light px-4 py-2 text-sm font-semibold text-intent-brand">
        {copy.birthLord}: {formatNakshatra(dasha.moon_nakshatra_lord)}
      </div>
    </div>
  );
}

function CurrentDashaCard({ dasha, language }: { dasha: KundaliDasha; language: "en" | "hi" }) {
  const copy = COPY[language];
  return (
    <section aria-label={copy.currentHeading} className="card-sacred mb-6 p-5 sm:p-6">
      <h3 className="mb-4 text-lg font-bold font-serif text-ish-dark">{copy.currentHeading}</h3>
      <div className="grid gap-3 sm:grid-cols-2">
        <CurrentPeriod
          label={copy.currentMahadasha}
          period={dasha.current_mahadasha}
          language={language}
        />
        <CurrentPeriod
          label={copy.currentAntardasha}
          period={dasha.current_antardasha}
          language={language}
        />
      </div>
    </section>
  );
}

function CurrentPeriod({
  label,
  period,
  language,
}: {
  label: string;
  period: EnginePeriod;
  language: "en" | "hi";
}) {
  return (
    <div className="rounded-lg bg-ish-accent-light p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-intent-brand">{label}</p>
      <p className="mt-1 text-xl font-bold font-serif text-ish-dark">
        {formatNakshatra(period.lord)}
      </p>
      <p className="mt-1 text-xs text-ish-text-muted">
        {formatCivilDate(period.start, language)} – {formatCivilDate(period.end, language)}
      </p>
    </div>
  );
}

function DashaSequence({ dasha, language }: { dasha: KundaliDasha; language: "en" | "hi" }) {
  const copy = COPY[language];
  const current = dasha.current_mahadasha;
  return (
    <section aria-label={copy.sequenceHeading} className="card-sacred mt-6 p-5 sm:p-6">
      <h3 className="mb-2 text-lg font-bold font-serif text-ish-dark">{copy.sequenceHeading}</h3>
      <p className="mb-5 text-sm text-ish-text-muted">{copy.sequenceBody}</p>
      <div className="space-y-3">
        {dasha.all_mahadashas.map((period, index) => (
          <DashaPeriod
            key={`${period.lord}:${period.start}`}
            period={period}
            index={index}
            active={period.lord === current.lord && period.start === current.start}
            language={language}
          />
        ))}
      </div>
    </section>
  );
}

/**
 * The antardasha timeline INSIDE the running mahadasha (ADR-0103, rule 91).
 *
 * `current_antardasha_sequence` has been on `KundaliResponse.dasha` since the
 * engine's `_calculate_antardasha` was wired in, and nothing rendered it — the
 * calculator showed only the single current antardasha and the 120-year
 * mahadasha list. So "when is my Venus–Saturn antardasha" was answerable only by
 * the 143 banned `/mahadasha/<lord>/<sub>-antardasha/` text pages, which
 * described the pairing in general and could never give a date.
 *
 * These dates are derived from the visitor's own mahadasha boundaries, which
 * come from their birth Moon's sidereal longitude. That is the uncopyable part,
 * and the reason this replaces the family rather than restating it.
 */
function AntardashaSequence({ dasha, language }: { dasha: KundaliDasha; language: "en" | "hi" }) {
  const copy = COPY[language];
  const sequence = dasha.current_antardasha_sequence;
  // Nothing computed renders nothing — an empty timeline card would read as
  // "your mahadasha has no antardashas", which is never true (rule 32).
  if (!sequence || sequence.length === 0) return null;
  const current = dasha.current_antardasha;
  const mahadashaLord = formatNakshatra(dasha.current_mahadasha.lord);
  return (
    <section
      aria-label={copy.antardashaHeading(mahadashaLord)}
      className="card-sacred mt-6 p-5 sm:p-6"
    >
      <h3 className="mb-2 text-lg font-bold font-serif text-ish-dark">
        {copy.antardashaHeading(mahadashaLord)}
      </h3>
      <p className="mb-5 text-sm text-ish-text-muted">{copy.antardashaBody}</p>
      <ol className="space-y-2">
        {sequence.map((period) => {
          const active = period.lord === current.lord && period.start === current.start;
          return (
            <li
              key={`${period.lord}:${period.start}`}
              className={`flex items-center justify-between rounded-lg p-3 ${
                active ? "bg-ish-accent-light" : ""
              }`}
            >
              <span className="text-sm font-semibold text-ish-dark">
                {mahadashaLord} – {formatNakshatra(period.lord)}
                {active && <span className="ml-2 text-xs">{copy.activeNow}</span>}
              </span>
              <span className="text-right text-xs text-ish-text-faint">
                {formatCivilDate(period.start, language)} – {formatCivilDate(period.end, language)}
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function DashaPeriod({
  period,
  index,
  active,
  language,
}: {
  period: EnginePeriod;
  index: number;
  active: boolean;
  language: "en" | "hi";
}) {
  const copy = COPY[language];
  const lord = formatNakshatra(period.lord);
  const details = DASHA_DETAILS[lord];
  return (
    <details className="group" open={active}>
      <summary className="flex cursor-pointer items-center justify-between rounded-lg p-3 transition-colors hover:bg-stone-50">
        <div className="flex items-center gap-3">
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-ish-accent-light text-xs font-bold text-intent-brand">
            {index + 1}
          </span>
          <span className="text-sm font-semibold text-ish-dark">
            {lord} Mahadasha
            {active && <span className="ml-2 text-xs">{copy.activeNow}</span>}
          </span>
        </div>
        <span className="text-right font-medium text-ish-text-faint">
          <span className="block text-xs">
            {period.years} {copy.years}
          </span>
          <span className="block text-xs">
            {formatCivilDate(period.start, language)} – {formatCivilDate(period.end, language)}
          </span>
        </span>
      </summary>
      {details && (
        <div className="pb-3 pl-14 pr-3 text-sm text-ish-text-muted">
          <p className="mb-2">{details.significations}</p>
          <p className="leading-relaxed">{details.nature}</p>
        </div>
      )}
    </details>
  );
}
